// Synthesized foley (Web Audio) + a real crumple sample. Selectable sound packs.
let ctx = null;
const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

export const isMuted = () => localStorage.getItem("pm_muted") === "1";
export const setMuted = (v) => localStorage.setItem("pm_muted", v ? "1" : "0");

export const getVolume = () => {
  const v = parseFloat(localStorage.getItem("pm_volume"));
  return isNaN(v) ? 0.8 : Math.min(1, Math.max(0, v));
};
export const setVolume = (v) => localStorage.setItem("pm_volume", String(v));

let masterGain = null;
const getMaster = () => {
  const ac = getCtx();
  if (!ac) return null;
  if (!masterGain) { masterGain = ac.createGain(); masterGain.connect(ac.destination); }
  masterGain.gain.value = getVolume();
  return masterGain;
};

const inQuietHours = () => {
  const q = localStorage.getItem("pm_quiet"); // "start-end" e.g. "22-7"
  if (!q) return false;
  const [s, e] = q.split("-").map(Number);
  const h = new Date().getHours();
  return s < e ? h >= s && h < e : h >= s || h < e;
};

const allowed = (ignoreMute) => ignoreMute || (!isMuted() && !inQuietHours());
const gate = (ignoreMute) => allowed(ignoreMute) && getCtx();

function playFile(src, vol, ignoreMute) {
  if (!allowed(ignoreMute)) return false;
  try {
    const a = new Audio(src);
    a.volume = Math.min(1, vol * getVolume());
    a.play().catch(() => {});
    return true;
  } catch {
    return false;
  }
}

// filtered noise burst -> paper texture (smooth attack + warmth lowpass = no clicks, less "digital")
function noise(duration, { type = "highpass", freq = 1200, gain = 0.4, decay = 0.9, q = 0.7, ignoreMute = false } = {}) {
  const ac = gate(ignoreMute);
  if (!ac) return;
  const n = Math.floor(ac.sampleRate * duration);
  const buf = ac.createBuffer(1, n, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, decay);
  const src = ac.createBufferSource();
  src.buffer = buf;
  const f = ac.createBiquadFilter();
  f.type = type; f.frequency.value = freq; f.Q.value = q;
  const warm = ac.createBiquadFilter();
  warm.type = "lowpass"; warm.frequency.value = 6800; warm.Q.value = 0.5;
  const g = ac.createGain();
  const t = ac.currentTime;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(gain, t + 0.007);
  g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
  src.connect(f); f.connect(warm); warm.connect(g); g.connect(getMaster() || ac.destination);
  src.start();
}

function tone(freq, dur, { type = "sine", gain = 0.15, ignoreMute = false } = {}) {
  const ac = gate(ignoreMute);
  if (!ac) return;
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = type; o.frequency.value = freq;
  const t = ac.currentTime;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(gain, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(getMaster() || ac.destination);
  o.start(t); o.stop(t + dur + 0.02);
}

// ---------- generic building blocks ----------
export const sounds = {
  peel() { doPlace("peel"); },
  peelOff() {
    // sticker peeling off cork: soft airy rip + gentle release
    noise(0.34, { type: "bandpass", freq: 1300, gain: 0.2, decay: 1.9, q: 0.9 });
    setTimeout(() => noise(0.18, { type: "bandpass", freq: 2200, gain: 0.12, decay: 1.5, q: 0.8 }), 110);
    setTimeout(() => tone(300, 0.07, { type: "sine", gain: 0.07 }), 240);
  },
  shuffle() { noise(0.16, { type: "bandpass", freq: 800, gain: 0.14, decay: 1.3, q: 0.7 }); },
  slap() { doPlace("slap"); },
  tack() { doPlace("tack"); },
  place() { doPlace(getPlaceSound()); },
  remove() { doRemove(getRemoveSound()); },
  crumple() { doRemove(getRemoveSound()); },
  // distinct arrival chime per inbound channel
  arrival(channel, ignoreMute = false) {
    const ch = channel || "manual";
    if (ch === "sms") {
      tone(740, 0.12, { type: "sine", gain: 0.12, ignoreMute });
      setTimeout(() => tone(988, 0.1, { type: "sine", gain: 0.08, ignoreMute }), 70);
    } else if (ch === "telegram") {
      tone(784, 0.12, { type: "sine", gain: 0.11, ignoreMute });
      setTimeout(() => tone(1046, 0.16, { type: "sine", gain: 0.09, ignoreMute }), 85);
    } else if (ch === "email") {
      tone(587, 0.14, { type: "sine", gain: 0.11, ignoreMute });
      setTimeout(() => tone(880, 0.18, { type: "sine", gain: 0.09, ignoreMute }), 95);
    } else {
      tone(880, 0.16, { type: "sine", gain: 0.1, ignoreMute });
    }
    setTimeout(() => noise(0.14, { type: "bandpass", freq: 1500, gain: 0.08, decay: 1.4, q: 0.8, ignoreMute }), 40);
  },
};

export const previewArrival = (channel) => sounds.arrival(channel, true);

// ---------- selectable packs ----------
export const PLACE_SOUNDS = [
  { key: "peel", label: "Peel + tap", hint: "soft peel then a little tap" },
  { key: "slap", label: "Paper slap", hint: "flat slap on cork" },
  { key: "pop", label: "Bubble pop", hint: "quick playful pop" },
  { key: "tack", label: "Thumbtack", hint: "crisp pin click" },
  { key: "chime", label: "Soft chime", hint: "gentle two-note ding" },
  { key: "silent", label: "Silent", hint: "no sound" },
];

export const REMOVE_SOUNDS = [
  { key: "foley", label: "Crumple (real)", hint: "recorded crumpling paper" },
  { key: "crunch", label: "Paper crunch", hint: "synth crunch + thud" },
  { key: "tear", label: "Fiber tear", hint: "sharp ripping paper" },
  { key: "whoosh", label: "Whoosh", hint: "airy sweep away" },
  { key: "thud", label: "Soft thud", hint: "low muffled drop" },
  { key: "silent", label: "Silent", hint: "no sound" },
];

export const getPlaceSound = () => localStorage.getItem("pm_place_sound") || "peel";
export const setPlaceSound = (k) => localStorage.setItem("pm_place_sound", k);
export const getRemoveSound = () => localStorage.getItem("pm_remove_sound") || "foley";
export const setRemoveSound = (k) => localStorage.setItem("pm_remove_sound", k);

export const getPlaceCustomUrl = () => localStorage.getItem("pm_place_custom_url") || "";
export const setPlaceCustomUrl = (u) => u ? localStorage.setItem("pm_place_custom_url", u) : localStorage.removeItem("pm_place_custom_url");
export const getRemoveCustomUrl = () => localStorage.getItem("pm_remove_custom_url") || "";
export const setRemoveCustomUrl = (u) => u ? localStorage.setItem("pm_remove_custom_url", u) : localStorage.removeItem("pm_remove_custom_url");

const CRUMPLE_SRC = "/sounds/crumple.mp3";

function doPlace(key, ignoreMute = false) {
  switch (key) {
    case "peel": noise(0.2, { type: "bandpass", freq: 1700, gain: 0.16, decay: 1.7, q: 0.9, ignoreMute }); setTimeout(() => tone(470, 0.06, { type: "sine", gain: 0.05, ignoreMute }), 120); return;
    case "slap": noise(0.12, { type: "lowpass", freq: 360, gain: 0.3, decay: 0.5, ignoreMute }); tone(130, 0.1, { type: "triangle", gain: 0.14, ignoreMute }); return;
    case "pop": tone(500, 0.1, { type: "sine", gain: 0.14, ignoreMute }); setTimeout(() => tone(760, 0.07, { type: "sine", gain: 0.08, ignoreMute }), 45); return;
    case "tack": tone(1500, 0.035, { type: "triangle", gain: 0.09, ignoreMute }); noise(0.05, { type: "bandpass", freq: 2600, gain: 0.08, decay: 1.0, q: 0.8, ignoreMute }); return;
    case "chime": tone(784, 0.22, { type: "sine", gain: 0.1, ignoreMute }); setTimeout(() => tone(1175, 0.26, { type: "sine", gain: 0.08, ignoreMute }), 90); return;
    case "custom":
      if (playFile(getPlaceCustomUrl(), 0.9, ignoreMute)) return;
      return doPlace("peel", ignoreMute);
    case "silent": default: return;
  }
}

function doRemove(key, ignoreMute = false) {
  switch (key) {
    case "foley":
      if (playFile(CRUMPLE_SRC, 0.85, ignoreMute)) return;
      // fall through to synth crunch if the sample can't play
    case "crunch":
      noise(0.3, { type: "bandpass", freq: 1400, gain: 0.24, decay: 0.7, q: 0.8, ignoreMute });
      setTimeout(() => noise(0.22, { type: "bandpass", freq: 2400, gain: 0.14, decay: 1.5, q: 0.8, ignoreMute }), 70);
      setTimeout(() => tone(85, 0.14, { type: "triangle", gain: 0.18, ignoreMute }), 170);
      return;
    case "tear":
      noise(0.36, { type: "bandpass", freq: 2200, gain: 0.22, decay: 1.8, q: 0.9, ignoreMute });
      setTimeout(() => noise(0.18, { type: "bandpass", freq: 3000, gain: 0.14, decay: 1.3, q: 0.8, ignoreMute }), 120);
      return;
    case "whoosh":
      noise(0.36, { type: "lowpass", freq: 650, gain: 0.26, decay: 0.6, ignoreMute });
      setTimeout(() => noise(0.2, { type: "lowpass", freq: 280, gain: 0.15, decay: 0.7, ignoreMute }), 90);
      return;
    case "thud":
      tone(75, 0.18, { type: "triangle", gain: 0.24, ignoreMute });
      noise(0.12, { type: "lowpass", freq: 220, gain: 0.18, decay: 0.5, ignoreMute });
      return;
    case "custom":
      if (playFile(getRemoveCustomUrl(), 0.9, ignoreMute)) return;
      return doRemove("crunch", ignoreMute);
    case "silent": default: return;
  }
}

// previews ignore mute so the picker always demos the sound
export const previewPlace = (key) => doPlace(key, true);
export const previewRemove = (key) => doRemove(key, true);
